import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { useAstrologerProfile } from '../../hooks/useAstrologerProfile';
import { useToast } from '../ui/ToastSystem';
import type { AstrologerProfile } from '../../types/professional';

type SettingsTab = 'Identidade' | 'Contato' | 'Documento' | 'Página Pública';
const TABS: SettingsTab[] = ['Identidade', 'Contato', 'Documento', 'Página Pública'];

const FONTS = ['Calibri', 'Georgia', 'Garamond', 'Times New Roman', 'Arial'];
const HEADER_TYPES = [
  { value: 'name_only',        label: 'Só nome', desc: 'Nome + título' },
  { value: 'name_and_contact', label: 'Nome + contato', desc: 'Nome, título e contatos em linha' },
  { value: 'full_header',      label: 'Cabeçalho completo', desc: 'Nome, título, e-mail, WhatsApp, site, Instagram' },
  { value: 'logo_only',        label: 'Logo', desc: 'Apenas seu logo' },
  { value: 'logo_and_contact', label: 'Logo + contato', desc: 'Logo à esquerda e contatos à direita' },
  { value: 'custom_message',   label: 'Mensagem personalizada', desc: 'Nome + frase escolhida por você' },
] as const;

function DocPreview({ profile }: { profile: Partial<AstrologerProfile> }) {
  const primary = profile.doc_primary_color ?? '#0A1628';
  const accent  = profile.doc_accent_color  ?? '#C49A22';
  const font    = profile.doc_font          ?? 'Calibri';

  return (
    <div style={{
      background: '#fff', color: '#111', fontFamily: font,
      borderRadius: '4px', padding: '1.5rem', fontSize: '9px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.3)', minHeight: '360px',
      lineHeight: 1.5,
    }}>
      {/* Header */}
      <div style={{ marginBottom: '0.75rem' }}>
        {(profile.doc_header_type === 'logo_only' || profile.doc_header_type === 'logo_and_contact') && profile.doc_logo_url ? (
          <img src={profile.doc_logo_url} alt="" style={{ height: '36px', marginBottom: '4px' }} />
        ) : null}
        <p style={{ margin: 0, fontWeight: 700, fontSize: '11px', color: primary }}>{profile.professional_name ?? 'Seu Nome'}</p>
        {profile.title && <p style={{ margin: 0, color: '#555' }}>{profile.title}</p>}
        {profile.doc_header_type === 'name_and_contact' && (
          <p style={{ margin: 0, color: '#555' }}>{[profile.contact_email, profile.whatsapp, profile.instagram].filter(Boolean).join(' · ')}</p>
        )}
        {profile.doc_header_type === 'custom_message' && profile.doc_custom_message && (
          <p style={{ margin: 0, fontStyle: 'italic', color: '#666' }}>"{profile.doc_custom_message}"</p>
        )}
        <hr style={{ borderColor: primary, marginTop: '6px', marginBottom: '6px' }} />
      </div>
      {/* Analysis title */}
      <p style={{ margin: '0 0 4px', fontWeight: 700, fontSize: '10px', color: primary, textTransform: 'uppercase' }}>Análise Astrológica</p>
      <p style={{ margin: '0 0 2px', fontWeight: 700, color: accent }}>Mapa Astral Completo</p>
      <p style={{ margin: '0 0 8px', color: '#555' }}>Cliente: Ana Souza · 13/03/1985 · São Paulo</p>
      <hr style={{ borderColor: '#ddd', margin: '6px 0' }} />
      {/* Body placeholder */}
      {[...Array(6)].map((_, i) => (
        <p key={i} style={{ margin: '0 0 3px', color: i % 3 === 0 ? primary : '#333', fontWeight: i % 3 === 0 ? 700 : 400 }}>
          {i % 3 === 0 ? '▸ Seção de análise' : 'Texto de exemplo que representa o conteúdo gerado para o cliente.'}
        </p>
      ))}
      {/* Footer */}
      <p style={{ marginTop: '1rem', textAlign: 'center', color: '#999', borderTop: '1px solid #eee', paddingTop: '6px' }}>
        {profile.doc_footer_text ?? 'Seu nome · sua URL'}
      </p>
      {/* Disclaimer */}
      {profile.doc_include_disclaimer && (
        <p style={{ color: '#999', fontStyle: 'italic', fontSize: '8px', marginTop: '4px' }}>
          {profile.doc_disclaimer_text ?? 'Esta análise tem caráter reflexivo e orientativo.'}
        </p>
      )}
    </div>
  );
}

export function ProfessionalSettings() {
  const { user } = useAuth();
  const { astrologerProfile, loading, refresh } = useAstrologerProfile();
  const { showToast } = useToast();
  const [tab, setTab]     = useState<SettingsTab>('Identidade');
  const [saving, setSaving] = useState(false);
  const [slugAvail, setSlugAvail] = useState<boolean | null>(null);
  const [slugChecking, setSlugChecking] = useState(false);

  // Local form state mirrors profile
  const [form, setForm] = useState<Partial<AstrologerProfile>>({});

  useEffect(() => {
    if (astrologerProfile) setForm(astrologerProfile);
  }, [astrologerProfile?.id]);

  function set(key: keyof AstrologerProfile, value: unknown) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  async function checkSlug(slug: string) {
    if (!slug || slug === astrologerProfile?.slug) { setSlugAvail(null); return; }
    setSlugChecking(true);
    const { count } = await supabase
      .from('astrologer_profiles')
      .select('id', { count: 'exact', head: true })
      .eq('slug', slug);
    setSlugAvail(count === 0);
    setSlugChecking(false);
  }

  async function handleSave() {
    if (!user) return;
    if (!astrologerProfile) {
      showToast({ message: 'Perfil de astrólogo não encontrado. Complete a ativação primeiro.', type: 'error' });
      return;
    }
    setSaving(true);
    try {
      // Exclude non-updatable fields
      const { id: _id, user_id: _uid, created_at: _ca, ...updatable } = form as AstrologerProfile;
      void _id; void _uid; void _ca;
      const { error } = await supabase
        .from('astrologer_profiles')
        .update(updatable)
        .eq('id', astrologerProfile.id);
      if (error) throw new Error(error.message);
      await refresh();
      showToast({ message: 'Configurações salvas ✦', type: 'success' });
    } catch (err) {
      showToast({ message: (err as Error).message, type: 'error' });
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div style={{ padding: '2rem', color: 'var(--text-muted)' }}>Carregando...</div>;
  if (!astrologerProfile) return (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
        Perfil de astrólogo não encontrado. Complete a ativação para acessar as configurações.
      </p>
      <a href="/profissional/ativar" className="btn btn-primary btn-sm">Ativar conta</a>
    </div>
  );

  return (
    <div>
      <h1 style={{ fontSize: 'clamp(1.25rem,3vw,1.5rem)', color: 'var(--text-primary)', margin: '0 0 1.5rem', fontFamily: 'var(--font-display)' }}>
        Configurações Profissionais
      </h1>

      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border-subtle)', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '0.6rem 1rem', background: 'none', border: 'none',
            borderBottom: `2px solid ${tab === t ? 'var(--petrol)' : 'transparent'}`,
            color: tab === t ? 'var(--petrol)' : 'var(--text-muted)',
            cursor: 'pointer', fontWeight: tab === t ? 600 : 400, fontSize: 'var(--text-label)',
          }}>
            {t}
          </button>
        ))}
      </div>

      {/* TAB: Identidade */}
      {tab === 'Identidade' && (
        <div className="card" style={{ maxWidth: '640px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem' }}>
            <div>
              <label className="form-label">Nome profissional *</label>
              <input className="form-input" value={form.professional_name ?? ''} onChange={e => set('professional_name', e.target.value)} />
            </div>
            <div>
              <label className="form-label">Título / especialização</label>
              <input className="form-input" value={form.title ?? ''} onChange={e => set('title', e.target.value)} placeholder="Astróloga Profissional" />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Bio <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({(form.bio ?? '').length}/500)</span></label>
              <textarea className="form-input" value={form.bio ?? ''} onChange={e => set('bio', e.target.value.slice(0, 500))} style={{ minHeight: '80px', resize: 'vertical', width: '100%' }} />
            </div>
            <div>
              <label className="form-label">Anos de experiência</label>
              <input className="form-input" type="number" min={0} max={60} value={form.years_experience ?? ''} onChange={e => set('years_experience', parseInt(e.target.value) || null)} />
            </div>
            <div>
              <label className="form-label">Especialidades (separadas por vírgula)</label>
              <input className="form-input" value={(form.specialties ?? []).join(', ')} onChange={e => set('specialties', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} placeholder="Karmica, Vocacional, Solar" />
            </div>
          </div>
        </div>
      )}

      {/* TAB: Contato */}
      {tab === 'Contato' && (
        <div className="card" style={{ maxWidth: '640px' }}>
          {[
            { field: 'contact_email' as keyof AstrologerProfile, showField: 'show_email' as keyof AstrologerProfile, label: 'E-mail de contato', type: 'email' },
            { field: 'phone'         as keyof AstrologerProfile, showField: 'show_phone' as keyof AstrologerProfile, label: 'Telefone', type: 'tel' },
            { field: 'whatsapp'      as keyof AstrologerProfile, showField: 'show_whatsapp' as keyof AstrologerProfile, label: 'WhatsApp', type: 'tel' },
            { field: 'website'       as keyof AstrologerProfile, showField: null, label: 'Website', type: 'url' },
            { field: 'instagram'     as keyof AstrologerProfile, showField: null, label: 'Instagram (@usuario)', type: 'text' },
            { field: 'tiktok'        as keyof AstrologerProfile, showField: null, label: 'TikTok (@usuario)', type: 'text' },
          ].map(({ field, showField, label, type }) => (
            <div key={field} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <div style={{ flex: 1 }}>
                <label className="form-label">{label}</label>
                <input className="form-input" type={type} value={(form[field] as string) ?? ''} onChange={e => set(field, e.target.value || null)} />
              </div>
              {showField && (
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: 'var(--text-caption)', color: 'var(--text-muted)', cursor: 'pointer', marginTop: '1.2rem', flexShrink: 0 }}>
                  <input type="checkbox" checked={!!form[showField]} onChange={e => set(showField, e.target.checked)} style={{ accentColor: 'var(--petrol)' }} />
                  Exibir no perfil público
                </label>
              )}
            </div>
          ))}
        </div>
      )}

      {/* TAB: Documento */}
      {tab === 'Documento' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', alignItems: 'start' }}>
          {/* Left: config */}
          <div className="card">
            <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem', fontSize: 'var(--text-label)' }}>Tipo de cabeçalho</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '1rem' }}>
              {HEADER_TYPES.map(ht => (
                <label key={ht.value} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: 'var(--text-label)', color: form.doc_header_type === ht.value ? 'var(--petrol)' : 'var(--text-secondary)' }}>
                  <input type="radio" name="header_type" value={ht.value} checked={form.doc_header_type === ht.value} onChange={() => set('doc_header_type', ht.value)} style={{ accentColor: 'var(--petrol)' }} />
                  <span><strong>{ht.label}</strong>, {ht.desc}</span>
                </label>
              ))}
            </div>

            {(form.doc_header_type === 'logo_only' || form.doc_header_type === 'logo_and_contact') && (
              <div style={{ marginBottom: '0.75rem' }}>
                <label className="form-label">URL do logo (PNG com fundo transparente)</label>
                <input className="form-input" type="url" value={form.doc_logo_url ?? ''} onChange={e => set('doc_logo_url', e.target.value || null)} placeholder="https://..." />
              </div>
            )}

            {form.doc_header_type === 'custom_message' && (
              <div style={{ marginBottom: '0.75rem' }}>
                <label className="form-label">Mensagem personalizada <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({(form.doc_custom_message ?? '').length}/150)</span></label>
                <input className="form-input" value={form.doc_custom_message ?? ''} onChange={e => set('doc_custom_message', e.target.value.slice(0, 150))} />
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <div>
                <label className="form-label">Cor primária</label>
                <input type="color" value={form.doc_primary_color ?? '#0A1628'} onChange={e => set('doc_primary_color', e.target.value)} style={{ width: '100%', height: '36px', borderRadius: '6px', border: '1px solid var(--border-subtle)', cursor: 'pointer' }} />
              </div>
              <div>
                <label className="form-label">Cor de destaque</label>
                <input type="color" value={form.doc_accent_color ?? '#C49A22'} onChange={e => set('doc_accent_color', e.target.value)} style={{ width: '100%', height: '36px', borderRadius: '6px', border: '1px solid var(--border-subtle)', cursor: 'pointer' }} />
              </div>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label className="form-label">Tipografia</label>
              <select className="form-input" value={form.doc_font ?? 'Calibri'} onChange={e => set('doc_font', e.target.value)}>
                {FONTS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label className="form-label">Texto do rodapé</label>
              <input className="form-input" value={form.doc_footer_text ?? ''} onChange={e => set('doc_footer_text', e.target.value || null)} placeholder="Seu Nome · seusite.com" />
            </div>

            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: '0.35rem', fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>
                <input type="checkbox" checked={!!form.doc_include_disclaimer} onChange={e => set('doc_include_disclaimer', e.target.checked)} style={{ accentColor: 'var(--petrol)' }} />
                Incluir disclaimer no documento
              </label>
              {form.doc_include_disclaimer && (
                <textarea className="form-input" value={form.doc_disclaimer_text ?? ''} onChange={e => set('doc_disclaimer_text', e.target.value)} style={{ width: '100%', minHeight: '60px', resize: 'vertical', fontSize: 'var(--text-caption)' }} />
              )}
            </div>
          </div>

          {/* Right: preview */}
          <div>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.5rem', textAlign: 'center' }}>Preview ao vivo</p>
            <DocPreview profile={form} />
          </div>
        </div>
      )}

      {/* TAB: Página Pública */}
      {tab === 'Página Pública' && (
        <div className="card" style={{ maxWidth: '560px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: '1rem', fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>
            <input type="checkbox" checked={!!form.is_public} onChange={e => set('is_public', e.target.checked)} style={{ accentColor: 'var(--petrol)', width: '16px', height: '16px' }} />
            Ativar página pública
          </label>

          <label className="form-label">Slug (URL pública)</label>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.35rem' }}>
            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', flexShrink: 0 }}>professya.com/astrologo/</span>
            <input
              className="form-input"
              value={form.slug ?? ''}
              onChange={e => { set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')); checkSlug(e.target.value); }}
              placeholder="seu-nome"
              style={{ flex: 1 }}
            />
          </div>
          {slugChecking && <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: 0 }}>Verificando...</p>}
          {!slugChecking && slugAvail === true  && <p style={{ fontSize: 'var(--text-caption)', color: '#4ade80', margin: 0 }}>✓ Disponível</p>}
          {!slugChecking && slugAvail === false && <p style={{ fontSize: 'var(--text-caption)', color: '#f87171', margin: 0 }}>✗ Já em uso</p>}
        </div>
      )}

      <div style={{ marginTop: '1.5rem' }}>
        <button className="btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Salvando...' : 'Salvar configurações'}
        </button>
      </div>
    </div>
  );
}
